import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  MarkerF,
  Circle,
  InfoWindowF,
} from "@react-google-maps/api";
import { useGoogleMaps } from "../hooks/useGoogleMaps";

export interface IMapMarker {
  lat: number;
  lng: number;
  title: string;
  subtitle?: string;
  note?: string;
}

interface IProps {
  latitude: number;
  longitude: number;
  height?: string;
  radius?: number;
  onLocationSelect?: (
    lat: number,
    lng: number,
    address?: string,
    city?: string,
  ) => void;
  markers?: IMapMarker[];
  zoom?: number;
  mainLabel?: string;
}

const containerStyle = {
  width: "100%",
  height: "400px",
};

const circleOptions = {
  strokeColor: "#4680FF",
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: "#4680FF",
  fillOpacity: 0.2,
};

const GoogleMapComponent: React.FC<IProps> = ({
  latitude,
  longitude,
  height = "400px",
  radius = 0,
  onLocationSelect,
  markers = [],
  zoom,
  mainLabel,
}) => {
  const { isLoaded } = useGoogleMaps();
  const [activeMarkerIndex, setActiveMarkerIndex] = useState<number | null>(
    null,
  );
  const [showMainInfo, setShowMainInfo] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  useEffect(() => {
    if (!mapRef.current || markers.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    bounds.extend({ lat: latitude || 0, lng: longitude || 0 });
    markers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
    mapRef.current.fitBounds(bounds, 60);
  }, [markers, latitude, longitude]);

  const center = {
    lat: latitude || 0,
    lng: longitude || 0,
  };

  const handleMapClick = useCallback(
    async (e: google.maps.MapMouseEvent) => {
      if (onLocationSelect && e.latLng) {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();

        try {
          // Reverse geocode to get address
          const geocoder = new google.maps.Geocoder();
          const response = await geocoder.geocode({ location: { lat, lng } });

          console.log("Geocoder response:", response);

          if (response.results && response.results[0]) {
            const addressComponents = response.results[0].address_components;
            const address = response.results[0].formatted_address;
            let city = "";

            for (const component of addressComponents) {
              if (component.types.includes("locality")) {
                city = component.long_name;
                break;
              }
              // Fallback to administrative_area_level_2 or sublocality
              if (
                !city &&
                (component.types.includes("administrative_area_level_2") ||
                  component.types.includes("sublocality"))
              ) {
                city = component.long_name;
              }
            }

            console.log(
              "Setting values - lat:",
              lat,
              "lng:",
              lng,
              "address:",
              address,
              "city:",
              city,
            );
            onLocationSelect(lat, lng, address, city);
          } else {
            console.warn("No geocoding results");
            onLocationSelect(lat, lng);
          }
        } catch (error) {
          console.error("Geocoding error:", error);
          // Still update coordinates even if geocoding fails
          onLocationSelect(lat, lng);
        }
      }
    },
    [onLocationSelect],
  );

  if (!isLoaded) {
    return (
      <div
        style={{ ...containerStyle, height }}
        className="bg-light d-flex align-items-center justify-content-center"
      >
        <div className="text-muted">Loading map...</div>
      </div>
    );
  }

  // Calculate zoom based on radius (smaller radius = more zoom)
  const getZoomForRadius = (r: number) => {
    if (r <= 0) return 15;
    if (r <= 50) return 17;
    if (r <= 100) return 16;
    if (r <= 200) return 15;
    if (r <= 500) return 14;
    return 13;
  };

  return (
    <GoogleMap
      mapContainerStyle={{ ...containerStyle, height }}
      center={center}
      zoom={zoom ?? (latitude && longitude ? getZoomForRadius(radius) : 2)}
      onClick={handleMapClick}
      onLoad={onMapLoad}
      options={{
        draggableCursor: onLocationSelect ? "crosshair" : undefined,
      }}
    >
      {/* Main incident pin — large red, most prominent */}
      <MarkerF
        position={center}
        zIndex={100}
        icon={{
          url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
          scaledSize: new google.maps.Size(48, 48),
          anchor: new google.maps.Point(24, 48),
        }}
        onClick={() => setShowMainInfo((v) => !v)}
      />
      {showMainInfo && (
        <InfoWindowF
          position={center}
          onCloseClick={() => setShowMainInfo(false)}
          options={{
            pixelOffset: new google.maps.Size(0, -48),
            disableAutoPan: true,
          }}
        >
          <div style={{ maxWidth: "200px", padding: "2px 4px" }}>
            <strong style={{ fontSize: "13px", color: "#d21034" }}>
              {mainLabel || "Incident location"}
            </strong>
          </div>
        </InfoWindowF>
      )}
      {radius > 0 && (
        <Circle center={center} radius={radius} options={circleOptions} />
      )}
      {/* Activity report pins — blue, smaller */}
      {markers.map((marker, index) => (
        <React.Fragment key={index}>
          <MarkerF
            position={{ lat: marker.lat, lng: marker.lng }}
            zIndex={10}
            icon={{
              url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
              scaledSize: new google.maps.Size(32, 32),
              anchor: new google.maps.Point(16, 32),
            }}
            onClick={() =>
              setActiveMarkerIndex(index === activeMarkerIndex ? null : index)
            }
          />
          {activeMarkerIndex === index && (
            <InfoWindowF
              position={{ lat: marker.lat, lng: marker.lng }}
              onCloseClick={() => setActiveMarkerIndex(null)}
              options={{
                pixelOffset: new google.maps.Size(0, -32),
                disableAutoPan: true,
              }}
            >
              <div style={{ maxWidth: "220px", padding: "2px 4px" }}>
                <strong style={{ fontSize: "13px" }}>{marker.title}</strong>
                {marker.note && (
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "12px",
                      color: "#333",
                    }}
                  >
                    {marker.note}
                  </p>
                )}
                {marker.subtitle && (
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "11px",
                      color: "#888",
                    }}
                  >
                    {marker.subtitle}
                  </p>
                )}
              </div>
            </InfoWindowF>
          )}
        </React.Fragment>
      ))}
    </GoogleMap>
  );
};

export default GoogleMapComponent;
